import React from 'react';
import {
  Box,
  Container,
  Typography,
  Stack,
  Card,
  List,
  ListItemText,
  Chip,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import Label from 'src/components/Label';
import { fDateTime } from 'src/utils/formatTime';
import { ArchiveBoxIcon, CalendarIcon, ClipboardDocumentIcon } from '@heroicons/react/24/outline';

export default function Adetails({ annonce }) {
  const url = process.env.REACT_APP_PUBLIC_URL;
  const { id, title, filePath, status = 1, category, annonceAttributes, stockage, createdAt } = annonce;
  const boxName = stockage && stockage.boxName ? stockage.boxName : 'pas de stockage';
  const image =
    filePath === null
      ? `${process.env.REACT_APP_PUBLIC_URL}/images/annonces/default/${category.defaultImage}`
      : `${process.env.REACT_APP_PUBLIC_URL}/images/annonces/${filePath}`;
  
  // Gérer les annonceAttributes qui peuvent être un objet ou un tableau
  let attributesArray = [];
  if (annonceAttributes) {
    if (Array.isArray(annonceAttributes)) {
      attributesArray = annonceAttributes;
    } else if (typeof annonceAttributes === 'object') {
      attributesArray = Object.values(annonceAttributes);
    }
  }
  
  const publicAttributes = attributesArray.filter((att) => att && att.isSecret === false);
  const secretAttributes = attributesArray.filter((att) => att && att.isSecret === true);
  
  // Debug pour voir les attributs
  

  // Fonction pour copier l'ID
  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
  
    } catch (err) {
      console.error('Erreur lors de la copie:', err);
    }
  };

  // Fonction pour obtenir la couleur du statut
  const getStatusColor = (status) => {
    switch (status) {
      case 'TROUVÉ':
        return { bg: '#10B981', text: 'white' };
      case 'PERDU':
        return { bg: '#F59E0B', text: 'white' };
      case 'RESTITUÉ':
        return { bg: '#3B82F6', text: 'white' };
      case 'ENVOYÉ':
        return { bg: '#8B5CF6', text: 'white' };
      case 'A_EXPEDIER':
        return { bg: '#F97316', text: 'white' };
      case 'PAYÉ':
        return { bg: '#059669', text: 'white' };
      default:
        return { bg: '#6B7280', text: 'white' };
    }
  };

  const statusColor = getStatusColor(status);
  const annonceDate = new Date(createdAt).toLocaleString('fr-FR', { 
    month: 'long', 
    day: 'numeric', 
    year: 'numeric' 
  });
 
  const ProductImgStyle = styled('img')({
    top: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    position: 'absolute',
  });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ p: 2 }}>
        {/* Image avec badges et informations */}
        <Box sx={{ position: 'relative', height: '300px', overflow: 'hidden', borderRadius: '8px', mb: 2 }}>
          <img
            src={image}
            alt={title}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
          
          {/* Badge statut en haut à gauche */}
          <Box sx={{
            position: 'absolute',
            top: '8px',
            left: '8px',
            backgroundColor: statusColor.bg,
            color: statusColor.text,
            padding: '4px 12px',
            borderRadius: '16px',
            fontSize: '0.75rem',
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            zIndex: 10,
          }}>
            <Box sx={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: 'rgba(255,255,255,0.3)' }} />
            {status}
          </Box>
          
          {/* Badge ID en haut à droite */}
          <Box sx={{
            position: 'absolute',
            top: '8px',
            right: '8px',
            backgroundColor: 'rgba(0,0,0,0.7)',
            color: 'white',
            padding: '4px 8px',
            borderRadius: '12px',
            fontSize: '0.75rem',
            fontWeight: 600,
            zIndex: 10,
          }}>
            #{id}
          </Box>

          {/* Informations de stockage en bas */}
          <Box sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            background: 'linear-gradient(to top, rgba(0,0,0,0.6), transparent)',
            padding: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}>
            <Box sx={{
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              backgroundColor: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <ArchiveBoxIcon style={{ width: '20px', height: '20px', color: '#6B7280' }} />
            </Box>
            <Typography variant="body2" sx={{ color: 'white', fontWeight: 500 }}>
              {boxName}
            </Typography>
          </Box>
        </Box>

        {/* Informations principales */}
        <Box sx={{ mb: 2 }}>
          {/* En-tête avec picto, ID et date */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {category?.image && (
                <img 
                  src={`/pictos/${category.image}.svg`} 
                  alt={category.name} 
                  style={{ width: '28px', height: '28px', marginRight: '8px' }} 
                />
              )}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Typography variant="body2" sx={{ color: '#374151', fontWeight: 700 }}>
                  #{id}
                </Typography>
                <Box
                  onClick={() => copyToClipboard(id.toString())}
                  sx={{
                    cursor: 'pointer',
                    padding: '4px',
                    borderRadius: '4px',
                    color: '#9CA3AF',
                    '&:hover': {
                      color: '#1b9098',
                      backgroundColor: 'rgba(27, 144, 152, 0.1)',
                    },
                    transition: 'all 0.2s ease',
                  }}
                  title="Copier l'ID"
                >
                  <ClipboardDocumentIcon style={{ width: '12px', height: '12px' }} />
                </Box>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', color: '#6B7280' }}>
              <CalendarIcon style={{ width: '16px', height: '16px', marginRight: '4px' }} />
              <Typography variant="caption">
                {annonceDate}
              </Typography>
            </Box>
          </Box>

          {/* Titre */}
          <Typography variant="h6" sx={{ color: '#1F2937', fontWeight: 600, mb: 2 }}>
            {title}
          </Typography>
        </Box>

        {/* Attributs publics avec style carte */}
        {publicAttributes.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ color: '#212B36', fontWeight: 600 }}>
                Caractéristiques
          </Typography>
            </Box>
            <Stack spacing={2}>
              {publicAttributes.map((attribute, index) => (
                <Box key={`public-${attribute.id || index}`} sx={{ 
                  padding: '12px', 
                  backgroundColor: '#F9FAFB', 
                  borderRadius: '8px',
                  border: '1px solid #DFE3E8',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}>
                  <Typography variant="body2" sx={{ color: '#637381', fontWeight: 500 }}>
                    {attribute.attribute}
        </Typography>
                  {attribute.uiType === "colorcheckbox" ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ 
                        width: '16px', 
                        height: '16px', 
                        borderRadius: '50%', 
                        backgroundColor: attribute.uiValue,
                        border: '1px solid #DFE3E8'
                      }} />
                      <Typography variant="body2" sx={{ color: '#212B36', fontWeight: 600 }}>
                        {attribute.attributeValue}
                      </Typography>
                    </Box>
                  ) : (
                    <Typography variant="body2" sx={{ color: '#212B36', fontWeight: 600 }}>
                      {attribute.attributeValue}
                    </Typography>
                  )}
                </Box>
          ))}
        </Stack>
          </Box>
        )}

        {/* Détails Secrets - Affiché seulement s'il y en a */}
        {secretAttributes.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, color: '#212B36', fontWeight: 600 }}>
              Détails Secrets
            </Typography>
            <Stack spacing={2}>
              {secretAttributes.map((attribute, index) => (
                <Box key={`secret-${attribute.id || index}`} sx={{ 
                  padding: '12px', 
                  backgroundColor: '#F9FAFB', 
                  borderRadius: '8px',
                  border: '1px solid #DFE3E8'
                }}>
                  <Typography variant="body2" sx={{ color: '#637381', fontWeight: 500, mb: 0.5 }}>
                    {attribute.attribute}
                  </Typography>
                  <Typography variant="body1" sx={{ color: '#212B36', fontWeight: 600 }}>
                    {attribute.attributeValue}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Box>
        )}
      </Box>
    </Box>
  );
}
